import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  BADGES,
  GITHUB_OAUTH_URL,
  GITHUB_TOKEN_SEARCH_PARAM,
} from "../constants";
import { AsyncTask } from "../domain";
import { useWeb3ProviderContext } from "./Web3Provider";
import { verifyGitHubCode } from "../adapters/api";

interface VerificationContext {
  gitHubVerification: AsyncTask<null, string>;
  verifyGitHubAccount: () => void;
}

const verificationContextNotReadyErrorMsg =
  "Verification context is not ready yet";

const verificationContext = createContext<VerificationContext>({
  gitHubVerification: { status: "pending" },
  verifyGitHubAccount: () => {
    throw Error(verificationContextNotReadyErrorMsg);
  },
});

export const VerificationContextProvider: FC<PropsWithChildren> = (props) => {
  const { connectedProvider } = useWeb3ProviderContext();
  const [gitHubVerification, setGitHubVerification] = useState<
    AsyncTask<null, string>
  >({
    status: "pending",
  });

  const verifyGitHubAccount = useCallback(() => {
    const params = new URLSearchParams({
      client_id: import.meta.env.VITE_APP_GITHUB_CLIENT_ID,
    });

    setGitHubVerification({ status: "loading" });
    window.location.href = `${GITHUB_OAUTH_URL}?${params.toString()}`;
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const code = query.get(GITHUB_TOKEN_SEARCH_PARAM);

    if (code && connectedProvider.status === "successful") {
      const gitHubBadge = BADGES.find((badge) => badge.key === "github");

      if (gitHubBadge) {
        setGitHubVerification({ status: "loading" });
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );

        verifyGitHubCode({
          ethereumAddress: connectedProvider.data.address,
          code: code,
        })
          .then(() => {
            setGitHubVerification({ status: "successful", data: null });
          })
          .catch((err) => {
            setGitHubVerification({ status: "failed", error: err.message });
          });
      }
    }
  }, [connectedProvider]);

  const value = useMemo(
    () => ({
      gitHubVerification,
      verifyGitHubAccount,
    }),
    [gitHubVerification, verifyGitHubAccount]
  );

  return <verificationContext.Provider value={value} {...props} />;
};

export const useVerificationContext = (): VerificationContext => {
  return useContext(verificationContext);
};
